import { resetMySharedAssetsList } from "@modules/shared-box-v2";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import useAbortController from "@hooks/useAbortController";
import VersionedComponent from "@components/VersionedComponent";
import { VERSION_CM_13766_SHARES_V3 } from "@cores/version";
import { Button, CircularProgress, Grid } from "@mzc-pdc/ui";
import EmptyList from "@components_v2/table-list/empty-list";
import TableList from "@components_v2/table-list/table-list";
import { togglePropertyPanel } from "@modules/propertyPanel";
import { IconDownload } from "@mzc-cloudplex/icons";
import { REQUEST_CATEGORY, TABLE_LIST_TYPE } from "@constants";
import ListTools from "@features/list-tools/list-tools";
import useDialog from "@hooks/useDialog";
import { Pagination } from "@components_v2/pgaination/pagination";
import TooltipButton from "@components_v2/tooltip-button/tooltip-button";
import { MySharingAssetsColumn } from "./columns-list-shared-v2";
import { withSearchQueryParamsContext } from "@components/params";
import * as yup from "yup";
import useDownloadJobs from "@hooks/useDownloadJobs";
import { listSharingAssetProjectsAPI } from "@modules/apis/shared-box-v2";
import { getMySharingAssets } from "@modules/shared-box-v2";
import DialogManageShareV2 from "@features/dialog/share/dialog-manage-share-v2";
import { siteMode } from "@cores/siteMode";

const ListSharedAssetsByMe = ({ searchQueryParams, updateFilterValueOptions }) => {
    const { spaceId, projectId } = useParams();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { getNewController, abortAll, handleApiResult } = useAbortController();
    const { dialog, openDialog, closeDialog } = useDialog();
    const { requestDownloadJob } = useDownloadJobs();
    const isLibrary = siteMode.get() === "CMS";

    const [tokenPaginationInfo, setTokenPaginationInfo] = useState({ currentPage: 0, data: [] });
    const [selectedItems, setSelectedItems] = useState([]);
    const [clickedItem, setClickedItem] = useState(null);
    const [actionTarget, setActionTarget] = useState(null);

    const stage = useSelector(({ stage }) => stage);
    const sharedBoxV2 = useSelector(({ sharedBoxV2 }) => sharedBoxV2);

    const onHandleChangeOffset = (page) => {
        setTokenPaginationInfo((prevState) => ({ currentPage: page, data: prevState.data }));
    };

    const onHandleChangeLimit = (limit) => {
        setTokenPaginationInfo((prevState) => ({ currentPage: 1, data: [] }));
        searchQueryParams.updateSearchQueryParams({
            action: "push",
            params: { ...searchQueryParams.params, limit },
        });
    };

    const loadSharedAssetsByMe = async () => {
        try {
            const signal = getNewController("mySharedAssets");
            const { currentPage, data: nextTokenList } = tokenPaginationInfo;

            const params = {
                nextToken: currentPage > 0 ? nextTokenList[currentPage] : undefined,
                limit: searchQueryParams?.params?.limit || 20,
                projectIds: searchQueryParams?.params?.projectIds?.map((project) => project.id)?.join(","),
            };

            const response = await dispatch(getMySharingAssets(params, signal));
            handleApiResult(response);
            nextTokenList[currentPage + 1] = response?.nextToken;
            setTokenPaginationInfo({ currentPage, data: nextTokenList });
        } catch (error) {
            console.error(error);
        }
    };

    const onHandleClickDownload = useCallback(
        (target) => {
            const targets = target ? [target] : selectedItems;

            // project.id별로 그룹화
            const groupedByProjectId = {};

            targets.forEach((item) => {
                const itemProjectId = item.project?.id || projectId;
                if (!groupedByProjectId[itemProjectId]) {
                    groupedByProjectId[itemProjectId] = [];
                }
                groupedByProjectId[itemProjectId].push(item);
            });

            // 각 프로젝트 그룹별로 다운로드 요청
            Object.entries(groupedByProjectId).forEach(([projectId, items]) => {
                const downloadRequestTargets = {
                    resourceId: items.length === 1 ? items[0].id : undefined,
                    resourceIds: items.length > 1 ? items.map((item) => item.id) : undefined,
                    projectId: projectId,
                    resourceName: items[0].name,
                };
                requestDownloadJob("ASSET", downloadRequestTargets, items.length, false);
            });

            setSelectedItems([]);
        },
        [selectedItems, projectId, requestDownloadJob],
    );

    const getListToolsButtons = useCallback(
        (isResponsive) => {
            return [
                <TooltipButton
                    key="download"
                    circled
                    title={t("common::label::Download", "Download")}
                    size={"small"}
                    onClickButton={() => {
                        onHandleClickDownload();
                    }}
                    buttonIcon={<IconDownload size={16} />}
                    buttonText={t("common::label::Download", "Download")}
                    isDivided={true}
                    isResponsive={isResponsive}
                />,
            ];
        },
        [selectedItems, requestDownloadJob],
    );

    const onHandleClickRefresh = () => {
        loadSharedAssetsByMe();
    };

    const onHandleClickShare = (item) => {
        setActionTarget(item);
        openDialog("share");
    };

    const onHandleClickCloseShare = () => {
        closeDialog();
        setActionTarget(null);
    };

    const pagination = useMemo(() => {
        return (
            <Pagination
                page={tokenPaginationInfo.currentPage}
                limit={searchQueryParams.params.limit}
                hasNextToken={tokenPaginationInfo.data[tokenPaginationInfo.currentPage + 1] !== undefined}
                onChangePage={onHandleChangeOffset}
                onChangeLimit={onHandleChangeLimit}
                onClickRefresh={onHandleClickRefresh}
            />
        );
    }, [tokenPaginationInfo, searchQueryParams?.params?.limit]);

    const parsedData = useMemo(() => {
        return sharedBoxV2?.data?.map(({ asset, id, ...rest }) => ({
            ...(asset ?? {}),
            id,
            assetId: asset?.id,
            expiresAt: rest?.option?.expiresAt,
            ...rest,
        }));
    }, [sharedBoxV2?.data]);

    const getFilterOptions = useCallback(async (filterIds) => {
        const results = {};

        // 각 필터 ID에 대해 병렬로 처리
        await Promise.all(
            filterIds.map(async (filterId) => {
                switch (filterId) {
                    case "projectIds": {
                        const projects = [];
                        let nextToken = null;
                        do {
                            const response = await listSharingAssetProjectsAPI(stage.endpoint, {
                                nextToken,
                                limit: 20,
                            });

                            projects.push(...response?.data?.results);
                            nextToken = response?.data?.nextToken;
                        } while (nextToken);

                        results[filterId] = projects.map(({ id, name }) => ({
                            id: id,
                            label: name,
                            value: id,
                            name: name,
                        }));
                        break;
                    }
                }
            }),
        );

        updateFilterValueOptions(results);
    }, []);

    useEffect(() => {
        searchQueryParams.updateSearchQueryParams({
            action: "replace",
            params: {
                ...searchQueryParams.params,
                limit: searchQueryParams?.params?.limit ?? 20,
                type: "SHARED_BY_ME",
            },
        });

        getFilterOptions(["projectIds"]);

        return () => {
            abortAll();
            dispatch(resetMySharedAssetsList());
        };
    }, []);

    useEffect(() => {
        if (!searchQueryParams?.params?.limit) {
            return;
        }
        loadSharedAssetsByMe();
    }, [searchQueryParams?.params, tokenPaginationInfo?.currentPage]);

    return (
        <VersionedComponent version={VERSION_CM_13766_SHARES_V3}>
            {dialog === "share" && (
                <DialogManageShareV2
                    selectedItem={{
                        id: actionTarget?.assetId,
                        name: actionTarget?.name,
                        summary: actionTarget,
                    }}
                    domain={"ASSET"}
                    onClickClose={onHandleClickCloseShare}
                />
            )}
            <ListTools
                domain={TABLE_LIST_TYPE.MY_SHARED_ASSETS}
                isCardView={false}
                getButtons={getListToolsButtons}
                pageCount={sharedBoxV2?.data?.length}
                numSelected={selectedItems.length}
                hasNextToken={tokenPaginationInfo.data[tokenPaginationInfo.currentPage] !== undefined}
                // onChangeNumSelected={onHandleChangeNumSelected}
                onChangePage={onHandleChangeOffset}
                onChangeLimit={onHandleChangeLimit}
                pagination={pagination}
                hiddenDivider={true}
            />
            {sharedBoxV2?.pending ? (
                <Grid item xs>
                    <Grid container alignItems={"center"} justifyContent={"center"} height={`100%`}>
                        <CircularProgress color="grey" />
                    </Grid>
                </Grid>
            ) : (
                <>
                    {parsedData?.length > 0 ? (
                        <TableList
                            keyPrefix={"my-shared-assets"}
                            type={"share_asset"}
                            columns={MySharingAssetsColumn}
                            data={parsedData}
                            selected={selectedItems}
                            clicked={clickedItem}
                            showColumns={true}
                            // onClickCheck={onHandleClickCheck}
                            onClickRow={(item) => {
                                setClickedItem(item);
                                if (isLibrary || !item.assetId) return;
                                dispatch(
                                    togglePropertyPanel({
                                        targetId: item.assetId,
                                        projectId: item.project.id,
                                        type: TABLE_LIST_TYPE.ASSET,
                                    }),
                                );
                            }}
                            actionButton={(item) => (
                                <Button
                                    size={"small"}
                                    color="secondary"
                                    variant="outlined"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onHandleClickShare(item);
                                    }}
                                    disabled={!item.assetId}
                                >
                                    {t("common::label::Shared management", "Shared management")}
                                </Button>
                            )}
                            actionButtonProps={{ width: 200 }}
                        />
                    ) : (
                        <EmptyList />
                    )}
                </>
            )}
        </VersionedComponent>
    );
};

export default withSearchQueryParamsContext({
    addedSchema: {
        projectIds: yup.array(),
        type: yup.string(),
    },
    setProps: true,
})(ListSharedAssetsByMe);
